import httpClient from "../http/http-client";
import { BillType, IBillDocument, IBillExtractionProgress, IFileUpload } from './file-upload.model';

export default class FileUploadService {
  http;

  constructor() {
    this.http = httpClient
  }

  async getAllUploads(detail: string): Promise<IFileUpload[]> {
    return this.http.get(`file-upload?details=${detail}`)
      .then(response => {
        return response.data.rows;
      })
      .catch(err => { throw err; })
  }

  async getAllUploadsWithPagination(detail: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc', queryString?: string): Promise<{ count: number, rows: IFileUpload[] }> {
    return this.http.get(`file-upload?details=${detail}&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}${queryString ? queryString : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; });
  }

  async getUpload(uploadId: string): Promise<IFileUpload> {
    return this.http.get(`file-upload/${uploadId}`)
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  async deleteUpload(uploadId: string, deleteAssociatedRecords: boolean = false): Promise<IFileUpload> {
    return this.http.get(`file-upload/${uploadId}/delete${deleteAssociatedRecords ? '?deleteAssociatedRecords=true' : '?deleteAssociatedRecords=false'}`)
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  async uploadFile(form: any, queryParam?: string | null): Promise<IFileUpload> {
    return this.http.post(`file-upload/upload-file${queryParam ? queryParam : ''}`, form)
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  // Whether bill (PDF/image) extraction is enabled in this environment. Used to
  // hide the "Upload bills" option rather than offering it and hitting a 503.
  async getBillExtractionStatus(): Promise<{ enabled: boolean }> {
    return this.http.get(`file-upload/bill-extraction-status`)
      .then(response => response.data)
      .catch(() => ({ enabled: false }))
  }

  async uploadBills(form: any, queryParam?: string | null): Promise<{ id: string, documentCount: number, message: string }> {
    return this.http.post(`file-upload/upload-bills${queryParam ? queryParam : ''}`, form)
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  async getBillDocuments(uploadId: string): Promise<{ billDocuments: IBillDocument[], progress: IBillExtractionProgress }> {
    return this.http.get(`file-upload/${uploadId}/bill-documents`)
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  async getBillDocumentPreviewUrl(billDocumentId: string): Promise<string> {
    return this.http.get(`file-upload/bill-document/${billDocumentId}/preview-url`)
      .then(response => {
        return response.data.url
      })
      .catch(err => { throw err; })
  }

  async updateBillDocumentType(billDocumentId: string, billType: BillType): Promise<any> {
    return this.http.patch(`file-upload/bill-document/${billDocumentId}`, { billType })
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  // "Not a credit note": gives the bill one blank row to fill in from the document.
  async addBillDocumentManualRow(billDocumentId: string): Promise<any> {
    return this.http.post(`file-upload/bill-document/${billDocumentId}/manual-row`, {})
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  // "Not cancelled by this credit note": releases a bill held back by a credit note in its upload.
  async dismissBillDocumentCancellation(billDocumentId: string): Promise<any> {
    return this.http.post(`file-upload/bill-document/${billDocumentId}/dismiss-cancellation`, {})
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  // The reviewer's call on a bill's renewable evidence: confirm gives its electricity rows a 0 supplier
  // rate (market-based); dismiss says the supply is not renewable, and undoes a confirm.
  async decideBillDocumentRenewable(billDocumentId: string, decision: 'confirm' | 'dismiss'): Promise<any> {
    return this.http.post(`file-upload/bill-document/${billDocumentId}/renewable`, { decision })
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  // "Keep both": a bill flagged as a possible duplicate is a different bill. It is not flagged again.
  async dismissBillDocumentDuplicate(billDocumentId: string): Promise<any> {
    return this.http.post(`file-upload/bill-document/${billDocumentId}/dismiss-duplicate`, {})
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  async uploadFileMappings(form: { fileUploadId: string, headings?: { [header: string]: string }, mappings?: { customHeading: string; mappedHeading: string }[] }): Promise<any> {
    return this.http.post(`file-upload/upload-file/mapping`, form)
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  async importFileUpload(fileUploadId: string, body: { importType: string, emissionType?: string }): Promise<any> {
    return this.http.post(`file-upload/import/${fileUploadId}`, body)
      .then(response => {
        return response.data
      })
      .catch(err => { throw err; })
  }

  async getUploadFileForMapping(fileUploadId: string, queryParams?: string): Promise<any> {
    return this.http.get(`file-upload/${fileUploadId}/mapping${queryParams ? queryParams : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  async getExcelTemplate(typeOfData: string, queryParams?: string): Promise<any> {
    return this.http.get(`file-upload/template/${typeOfData}${queryParams ? queryParams : ''}`, { responseType: 'blob' })
      .then(response => {
        return response
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  async getNumberOfSheetsInExcelFile(form: any): Promise<{ totalNumberOfSheets: number, sheets: { sheetNumber: number, sheetName: string }[] }> {
    return this.http.post(`file-upload/number-of-sheets`, form)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  async checkFileUploadProgress(fileUploadId: string): Promise<IFileUpload> {
    return this.http.get(`file-upload/${fileUploadId}/progress`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  async checkFileUploadCommitProgress(fileUploadId: string): Promise<IFileUpload> {
    return this.http.get(`file-upload/${fileUploadId}/commit-progress`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  async reRunValidation(uploadId: string): Promise<any> {
    return this.http.post(`file-upload/${uploadId}/re-run-validation`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }
}